import { getAuthUser } from "@/lib/auth";
import type { OrgUnitHierarchy } from "@/components/org-unit/OrgUnitTree";
import type { DashTab, FacilityTimeCounts, FacilityTimeRow, NewIndicatorRow } from "./types";

const CACHE_VERSION = 1;

/** Cached views older than this are treated as stale and dropped on read. */
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;

/**
 * The last run is kept per signed-in user so a shared browser never shows one
 * person's analysis to the next. The public embed view gets its own scope.
 */
export const cacheScopeKey = (publicMode: boolean): string => {
  if (publicMode) return "public";
  const user = getAuthUser<{ id?: number | string; email?: string; username?: string }>();
  const identity = user?.email || (user?.id != null ? String(user.id) : "") || user?.username || "";
  return identity ? `auth:${identity.trim().toLowerCase()}` : "auth:anonymous";
};

const storageKey = (scope: string) => `dash_analysis_cache_v${CACHE_VERSION}_${scope}`;
const activeTabKey = (scope: string) => `dash_active_tab_v${CACHE_VERSION}_${scope}`;

/**
 * Which tab was last open. Kept apart from the data cache so switching tabs never
 * rewrites (and therefore never falsifies) the "last run" timestamp.
 */
export const loadActiveTab = (scope: string): DashTab | null => {
  try {
    const raw = localStorage.getItem(activeTabKey(scope));
    return raw === "new-indicators" || raw === "combined" ? raw : null;
  } catch {
    return null;
  }
};

export const saveActiveTab = (scope: string, tab: DashTab) => {
  try {
    localStorage.setItem(activeTabKey(scope), tab);
  } catch {
    // storage unavailable
  }
};

const EMPTY_HIERARCHY: OrgUnitHierarchy = {
  moh: "",
  region: "",
  zone: "",
  woreda: "",
  phcu: "",
  level6: "",
  level7: "",
};

export interface TimeCache {
  hasRun: boolean;
  indicator: string;
  hierarchy: OrgUnitHierarchy;
  year: string;
  month: string;
  rows: FacilityTimeRow[];
  seriesRows: FacilityTimeRow[];
  methodRows: FacilityTimeRow[];
  counts: FacilityTimeCounts;
}

export interface NewIndicatorCache {
  hasRun: boolean;
  year: string;
  month: string;
  baselineMonths: number;
  orgUnits: string[];
  rows: NewIndicatorRow[];
}

export interface DashCache {
  version: number;
  savedAt: number;
  time: TimeCache;
  newIndicators: NewIndicatorCache;
}

export const createEmptyDashCache = (): DashCache => ({
  version: CACHE_VERSION,
  savedAt: 0,
  time: {
    hasRun: false,
    indicator: "",
    hierarchy: { ...EMPTY_HIERARCHY },
    year: "",
    month: "",
    rows: [],
    seriesRows: [],
    methodRows: [],
    counts: {},
  },
  newIndicators: {
    hasRun: false,
    year: "",
    month: "",
    baselineMonths: 12,
    orgUnits: [],
    rows: [],
  },
});

const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const asString = (value: unknown, fallback = "") => (typeof value === "string" ? value : fallback);

const asHierarchy = (value: unknown): OrgUnitHierarchy => {
  const raw = asRecord(value);
  return {
    moh: asString(raw.moh),
    region: asString(raw.region),
    zone: asString(raw.zone),
    woreda: asString(raw.woreda),
    phcu: asString(raw.phcu),
    level6: asString(raw.level6),
    level7: asString(raw.level7),
  };
};

const normalize = (raw: unknown): DashCache => {
  const parsed = asRecord(raw);
  const time = asRecord(parsed.time);
  const newIndicators = asRecord(parsed.newIndicators);
  const baseline = Number(newIndicators.baselineMonths);

  return {
    version: CACHE_VERSION,
    savedAt: Number(parsed.savedAt) || 0,
    time: {
      hasRun: time.hasRun === true,
      indicator: asString(time.indicator),
      hierarchy: asHierarchy(time.hierarchy),
      year: asString(time.year),
      month: asString(time.month),
      rows: asArray<FacilityTimeRow>(time.rows),
      seriesRows: asArray<FacilityTimeRow>(time.seriesRows),
      methodRows: asArray<FacilityTimeRow>(time.methodRows),
      counts: asRecord(time.counts) as FacilityTimeCounts,
    },
    newIndicators: {
      hasRun: newIndicators.hasRun === true,
      year: asString(newIndicators.year),
      month: asString(newIndicators.month),
      baselineMonths: Number.isFinite(baseline) && baseline > 0 ? baseline : 12,
      orgUnits: asArray<string>(newIndicators.orgUnits).filter((item) => typeof item === "string"),
      rows: asArray<NewIndicatorRow>(newIndicators.rows),
    },
  };
};

const hasCachedView = (cache: DashCache) =>
  cache.time.seriesRows.length > 0 || cache.time.rows.length > 0 || cache.newIndicators.rows.length > 0;

const clearDashCache = (scope: string) => {
  try {
    localStorage.removeItem(storageKey(scope));
  } catch {
    // storage unavailable (private mode, disabled cookies)
  }
};

/**
 * Reads the last successful run so the page can render it immediately on mount.
 * Returns null when nothing usable is stored, when the payload was written by a
 * different cache version, or when it has aged out past the TTL.
 */
export const loadDashCache = (scope: string): DashCache | null => {
  try {
    const raw = localStorage.getItem(storageKey(scope));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || parsed.version !== CACHE_VERSION) {
      clearDashCache(scope);
      return null;
    }

    const savedAt = Number(parsed.savedAt) || 0;
    if (!savedAt || Date.now() - savedAt > CACHE_TTL_MS) {
      clearDashCache(scope);
      return null;
    }

    const cache = normalize(parsed);
    return hasCachedView(cache) ? cache : null;
  } catch {
    return null;
  }
};

/**
 * Persists the last run. localStorage is capped per origin, so oversized payloads
 * are progressively trimmed (dataset table first, then the extra anomaly rows the
 * chart draws markers from) rather than dropped entirely.
 */
export const saveDashCache = (scope: string, cache: DashCache): void => {
  const attempts: Array<() => DashCache> = [
    () => cache,
    () => ({ ...cache, newIndicators: { ...cache.newIndicators, rows: [] } }),
    () => ({
      ...cache,
      newIndicators: { ...cache.newIndicators, rows: [] },
      time: { ...cache.time, rows: cache.time.rows.slice(0, 200) },
    }),
    () => ({
      ...cache,
      newIndicators: { ...cache.newIndicators, rows: [] },
      time: { ...cache.time, rows: [], methodRows: [] },
    }),
  ];

  for (const build of attempts) {
    try {
      localStorage.setItem(storageKey(scope), JSON.stringify(build()));
      return;
    } catch {
      // try a smaller payload
    }
  }

  clearDashCache(scope);
};
